import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { apiLogin, apiLogout, apiMe, type UserRole } from './authApi';
import { settingsRepository } from '../../data/repositories/settingsRepository';
import { startSyncScheduler, stopSyncScheduler } from '../../data/sync/syncScheduler';
import { resetLocalDatabase } from '../../data/resetLocalDatabase';

/**
 * Authentication state, backed by the real server session.
 *
 * The authority is the `HttpOnly` cookie, checked via `GET /api/auth/me`.
 * JavaScript cannot read that cookie, and nothing in this file tries to —
 * the browser attaches it, the server decides. See AUTH-ARCHITECTURE.md §10–§12.
 *
 * The one nuance is offline: a cookie that cannot be checked is not the same
 * as a cookie that was rejected. When the server is unreachable, the app falls
 * back to `auth.lastVerified` — a timestamp, not a credential — which permits
 * continuing an already-established session for a bounded window and nothing
 * more. There is no offline login: an unverifiable device with no recent
 * verification gets an honest "can't verify" screen, never a local password
 * check.
 */

/** AUTH-ARCHITECTURE.md §11 — approved decision #2. */
export const OFFLINE_GRACE_PERIOD_MS = 7 * 24 * 60 * 60 * 1000;

export type AuthStatus =
  /** The initial `/api/auth/me` check has not resolved yet. */
  | 'checking'
  /** Server confirmed the session on this load. */
  | 'authenticated'
  /** Server unreachable, but verified within the grace period. */
  | 'offline-authenticated'
  /** Server said no, or we have never been verified. Show the login screen. */
  | 'unauthenticated'
  /** Server unreachable AND no usable verification cache. */
  | 'offline-unverifiable'
  /**
   * The server confirmed the session, but this device could not be safely
   * prepared for it — `resetLocalDatabase()` failed (and its one automatic
   * retry also failed) while switching local storage to this account. Phase
   * G fix design: deliberately distinct from `offline-unverifiable` — the
   * problem here is local, not connectivity, and granting `'authenticated'`
   * anyway would let the sync scheduler start against unverified, possibly
   * contaminated local data. See `resetLocalDatabase.ts`'s own doc.
   */
  | 'reset-failed';

interface AuthContextValue {
  status: AuthStatus;
  /** False until the initial check resolves. */
  ready: boolean;
  /** True when the app should be rendered — either kind of authenticated. */
  isAuthenticated: boolean;
  /** True specifically when running on the offline grace period. */
  isOffline: boolean;
  /**
   * Authorization (Master Phase 11), orthogonal to authentication. Null while
   * unresolved or unauthenticated. Never inferred from the email — always
   * sourced from `/api/auth/me`, live when online, from the offline cache
   * (§11) only while continuing an already-established session.
   */
  role: UserRole | null;
  /**
   * True only in the narrow window right after bootstrap, before the
   * one-time provisional→permanent password transition (§6) completes.
   * Deliberately not cached offline — see `LastVerifiedRecord`'s own comment.
   */
  mustChangePassword: boolean;
  /** Resolves to whether the attempt succeeded. Never throws. */
  login: (attempt: LoginAttempt) => Promise<LoginOutcome>;
  logout: () => void;
  /** Re-run the authoritative server check. Used on reconnect/refocus. */
  revalidate: () => Promise<void>;
}

export interface LoginAttempt {
  email: string;
  password: string;
}

export type LoginOutcome =
  | 'success'
  | 'invalid-credentials'
  | 'pending-approval'
  /** Email-verification addendum — see authApi.ts's `LoginEmailUnverified`. */
  | 'email-unverified'
  | 'network-error';

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * Ensures local data is safe to treat as belonging to `email` before sync is
 * ever allowed to start — Phase G fix design. Runs `resetLocalDatabase()`
 * (which itself is coalescing and durably state-tracked — see that
 * function's own doc), and — only once it has actually succeeded — records
 * the new owner. Ownership assignment deliberately happens HERE, in the auth
 * layer, not inside `resetLocalDatabase()` itself: the reset function only
 * ever needs to answer "clear the tables, and report whether it worked,"
 * never "whose data this becomes," which is an authentication decision, not
 * a database one.
 *
 * Retries exactly once on failure — a single, bounded, non-recursive retry
 * (a plain two-iteration loop, not a call to itself), covering an ordinary
 * transient IndexedDB hiccup without leaving a genuinely broken device stuck
 * retrying forever. Returns `false` (leaving `localResetState` at `'failed'`
 * from `resetLocalDatabase()`'s own error handling) only once both attempts
 * have failed — the caller must not proceed to `status: 'authenticated'` in
 * that case.
 *
 * `isStale` — the `revalidate()` freshness guard (see that function's own
 * doc). `resetLocalDatabase()` itself is left to run to completion either
 * way (it's coalescing — an in-flight or freshly-triggered reset is real,
 * possibly-shared work, not a stale conclusion), but the ownership write
 * immediately after it is exactly the kind of commit a superseded caller
 * must never make — checked right here, not left to the caller, because a
 * caller that only inspects this function's return value would still be too
 * late: this function does the actual write before returning.
 */
async function settleLocalDatabaseFor(email: string, isStale: () => boolean): Promise<boolean> {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      await resetLocalDatabase();
      if (isStale()) return false;
      await settingsRepository.setLocalDataOwner({ email });
      return true;
    } catch {
      // One bounded retry, then give up — see this function's own doc.
    }
  }
  return false;
}

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [status, setStatus] = useState<AuthStatus>('checking');
  const [role, setRole] = useState<UserRole | null>(null);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  /**
   * Set when a logout could not reach the server, so the session is still live
   * server-side. Retried on reconnect (§11) — the local UI is already locked
   * either way, so this is cleanup, not enforcement.
   */
  const logoutRevocationPending = useRef(false);
  const mounted = useRef(true);

  /**
   * Freshness guard against overlapping `revalidate()` invocations —
   * independent of, and a separate defect from, the Phase G reset race.
   * Nothing previously stopped an OLDER, slower invocation (e.g. one started
   * by the online/visibilitychange effect, whose `apiMe()` is still in
   * flight from before a logout/login happened) from committing its stale
   * conclusions AFTER a NEWER one (a fresh login, or a fresh logout) had
   * already established the correct state — a last-writer-wins race with no
   * relation to which invocation actually reflects reality.
   *
   * Every `revalidate()` call increments this and captures its own value;
   * every commit point inside `revalidate()` — both `settingsRepository`
   * writes (`status`/`role`/`owner`/`lastVerified` all indirectly derive
   * from these) and the React state setters — checks the captured value
   * against the current one immediately before writing, and bails out
   * (writing nothing) if a newer call has since started. `logout()` also
   * bumps this directly, even though it never calls `revalidate()` itself:
   * logout is an authentication-lifecycle transition exactly like a new
   * login is, and an in-flight revalidate() that started before it must not
   * be able to resolve afterward and silently resurrect the account that
   * was just logged out of — traced and confirmed as a real, simpler
   * variant of the same race (no second login even required).
   *
   * Deliberately NOT a mutex: a superseded invocation's own network/reset
   * work (`apiLogout()`, `resetLocalDatabase()`) is left to run to
   * completion rather than cancelled — those are real, possibly-shared side
   * effects (the reset is itself coalescing), not conclusions. Nothing here
   * makes any operation wait behind another; it only decides who is allowed
   * to write.
   */
  const revalidationGenerationRef = useRef(0);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /**
   * The authoritative check. A 200 or a 401 is a real answer and always wins;
   * only a genuine network failure falls back to the cache.
   */
  const revalidate = useCallback(async (): Promise<void> => {
    // Claims this invocation as the newest — see `revalidationGenerationRef`'s
    // own doc. Captured once, synchronously, before any await, so a LATER
    // call (which increments the same ref) always makes this one's captured
    // value stale from that point on.
    const myGeneration = ++revalidationGenerationRef.current;
    const isStale = (): boolean => myGeneration !== revalidationGenerationRef.current;

    if (logoutRevocationPending.current) {
      // A logout that never reached the server. Finish that first, so a
      // reconnect can't resurrect a session the user already ended. The
      // network call itself always runs — only the resulting commit is
      // guarded, since a genuinely newer operation may have already
      // established a real session by the time this resolves.
      const outcome = await apiLogout();
      if (outcome === 'revoked') logoutRevocationPending.current = false;
      if (isStale() || !mounted.current) return;
      setStatus('unauthenticated');
      setRole(null);
      setMustChangePassword(false);
      return;
    }

    const result = await apiMe();
    if (!mounted.current || isStale()) return;

    if (result.outcome === 'authenticated') {
      // Cross-account data-isolation gate (Phase G-A/G-A.1, hardened by the
      // Phase G race-condition fix design): `db` is one shared Dexie
      // instance per browser origin, not partitioned per account (see
      // `resetLocalDatabase`'s own doc). If the account this `/me` check
      // just confirmed differs from whichever account `localDataOwner` says
      // currently occupies the local data tables, that data belongs to that
      // OTHER account and must not be treated as this one's — reached both
      // when `logout()`'s own reset never got to run (a crash, a
      // force-quit, or re-authenticating directly as someone else without
      // an intervening logout) AND when the session merely lapsed in
      // between (expired, revoked elsewhere) and a DIFFERENT account
      // authenticates next; see `localDataOwner`'s own doc for why it — not
      // `lastVerified` — is the correct signal for this specific
      // comparison. `logout()` deliberately leaves `localDataOwner`
      // untouched (see its own comment) precisely so this comparison is a
      // pure, timing-independent value check — there is no window where a
      // premature clear could hide a real mismatch, which was the actual
      // Phase G defect.
      //
      // `resetState !== 'idle'` covers the case a value-only comparison
      // can't: a *stale* `in-progress`/`failed` marker left by an
      // interrupted or previously-failed reset, even when `owner` happens
      // to already equal this account's email (its value may predate that
      // interrupted attempt and not reflect the tables' real, unverified
      // state) — see `localResetState`'s own doc.
      //
      // Either way, `settleLocalDatabaseFor` is awaited here, before
      // `status` is ever set to `'authenticated'` below — and
      // `'authenticated'` is the one thing that gates `startSyncScheduler()`
      // (see that effect further down) — so a sync cycle is structurally
      // blocked from starting until this finishes, not merely expected to
      // follow logout (or the previous session ending) by convention.
      // `sync/syncEngine.ts`'s `runSyncCycle()` independently re-checks the
      // same three settings itself, as a backstop against any future caller
      // that bypasses this component entirely.
      const owner = await settingsRepository.getLocalDataOwner();
      const resetState = await settingsRepository.getLocalResetState();
      if (isStale()) return;
      const needsReset = resetState !== 'idle' || (owner !== null && owner.email !== result.email);

      if (needsReset) {
        // `settleLocalDatabaseFor` itself checks freshness immediately
        // before its own ownership write — checking only the return value
        // here would be too late, since that write may already have
        // happened by the time this line runs.
        const settled = await settleLocalDatabaseFor(result.email, isStale);
        if (isStale()) return;
        if (!settled) {
          // Never grant an authenticated, sync-eligible session over local
          // data that could not be confirmed safe — see `AuthStatus`'s own
          // doc for `'reset-failed'`. (`settled` is also `false` when this
          // invocation was superseded mid-reset, but `isStale()` above
          // already caught that case and returned — so reaching here always
          // means a genuine, still-current failure.)
          if (mounted.current) {
            setRole(null);
            setMustChangePassword(false);
            setStatus('reset-failed');
          }
          return;
        }
      } else {
        // No reset was needed (same account continuing, or a fresh device
        // with no prior owner) — still record ownership, exactly as before.
        await settingsRepository.setLocalDataOwner({ email: result.email });
      }

      if (isStale()) return;
      await settingsRepository.setLastVerified({ email: result.email, verifiedAt: Date.now(), role: result.role });

      if (isStale() || !mounted.current) return;
      setRole(result.role);
      setMustChangePassword(result.mustChangePassword);
      setStatus('authenticated');
      return;
    }

    if (result.outcome === 'unauthenticated') {
      // A definite server "no" — the session expired, was revoked, or was
      // ended elsewhere. The cache is now meaningless and must not survive.
      // The sync cursor is cleared here too: `db` is one shared Dexie
      // instance, not partitioned per account, so whichever account signs
      // in next must not inherit this account's pull position.
      await settingsRepository.clearLastVerified();
      await settingsRepository.clearSyncCursor();
      // `localDataOwner` is deliberately NOT cleared here (Phase G-A.1) —
      // this was the actual gap the original fix left open. Whatever
      // account's data is still sitting in the local tables at this exact
      // moment hasn't gone anywhere just because the session lapsed; only
      // once *some* account next authenticates can the app tell whether
      // that data is still correctly theirs, and the `authenticated`
      // branch above is what makes that comparison and resets if not. This
      // device is not yet reset here — merely locked, as it always was —
      // because the account that will next appear could well be the SAME
      // one, re-authenticating after a routine expiry, and destroying its
      // own not-yet-synced local changes purely because a token lapsed
      // would be a real, avoidable data-loss regression, not a fix.
      // Sync itself cannot run while unauthenticated regardless
      // (`startSyncScheduler()` is gated on `status === 'authenticated'`
      // below), so leaving the data in place here creates no leak risk on
      // its own — the leak only ever happens at the point a DIFFERENT
      // account subsequently authenticates, and that path is covered above.
      if (isStale() || !mounted.current) return;
      setStatus('unauthenticated');
      setRole(null);
      setMustChangePassword(false);
      return;
    }

    // Network error only. Fall back to the bounded grace period.
    const cached = await settingsRepository.getLastVerified().catch(() => null);
    if (!mounted.current || isStale()) return;

    const withinGrace =
      cached !== null && Date.now() - cached.verifiedAt < OFFLINE_GRACE_PERIOD_MS;
    setRole(withinGrace ? (cached?.role ?? null) : null);
    // mustChangePassword can only ever be resolved by a fresh, online /me
    // response (§8 of the approved plan) — it stays false through any
    // offline continuation, exactly like it would for an ordinary account.
    setMustChangePassword(false);
    setStatus(withinGrace ? 'offline-authenticated' : 'offline-unverifiable');
  }, []);

  useEffect(() => {
    void revalidate();
  }, [revalidate]);

  /**
   * Reconciliation triggers (§11): connectivity returning, and the app coming
   * back to the foreground. No polling — these are the moments where the
   * answer can actually have changed.
   */
  useEffect(() => {
    function onOnline(): void {
      void revalidate();
    }
    function onVisibility(): void {
      if (document.visibilityState === 'visible') void revalidate();
    }

    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [revalidate]);

  /**
   * The sync engine only ever runs against a live, authenticated server
   * session — deliberately `'authenticated'` only, not
   * `'offline-authenticated'` (LiftX Sync Blueprint §9): there is no server
   * to sync against on the offline grace path, and starting the scheduler
   * there would just be a guaranteed-failing fetch every cycle. Also gated
   * on `!mustChangePassword`: the account isn't settled yet during the
   * forced provisional→permanent password transition (§6), and syncing data
   * for it before that completes has no defined meaning.
   */
  useEffect(() => {
    if (status !== 'authenticated' || mustChangePassword) return;
    startSyncScheduler();
    return () => stopSyncScheduler();
  }, [status, mustChangePassword]);

  const login = useCallback(async (attempt: LoginAttempt): Promise<LoginOutcome> => {
    const result = await apiLogin(attempt.email, attempt.password);

    if (result.outcome === 'success') {
      // A fresh login supersedes any pending revocation — the old session is
      // gone from this device's perspective and a new one has replaced it.
      logoutRevocationPending.current = false;
      // login.ts's own response carries only `{email}` (Master Phase 11 —
      // "the frontend AuthGate, not login.ts, is what gates on
      // mustChangePassword"). role/mustChangePassword are always sourced
      // from /api/auth/me, so revalidate() immediately, rather than
      // duplicating that lookup here.
      await revalidate();
      return 'success';
    }

    return result.outcome;
  }, [revalidate]);

  const logout = useCallback((): void => {
    // Invalidates any `revalidate()` invocation already in flight — see
    // `revalidationGenerationRef`'s own doc. logout() never calls
    // `revalidate()` itself, but it is exactly the same kind of
    // authentication-lifecycle transition: an older, still-pending
    // `revalidate()` (e.g. one the online/visibilitychange effect started
    // before this) must not be able to resolve after this point and
    // silently resurrect the account being logged out of. Without this, a
    // stale invocation would read `owner` as still unchanged (this function
    // deliberately doesn't touch it — see below) and `lastVerified` as
    // definitely gone (this function DOES clear that), find no mismatch for
    // its own stale email, and re-write `lastVerified` + `status:
    // 'authenticated'` right back — a simpler, second-account-free variant
    // of the same race, not merely a hypothetical one.
    revalidationGenerationRef.current += 1;

    // Local lock is immediate and unconditional — it must not wait for, or
    // depend on, the network (§11).
    setStatus('unauthenticated');
    setRole(null);
    setMustChangePassword(false);
    logoutRevocationPending.current = true;
    void settingsRepository.clearLastVerified();
    // Same reasoning as `revalidate()`'s definite-unauthenticated branch —
    // a leftover cursor must not be inherited by whichever account signs in
    // next on this device.
    void settingsRepository.clearSyncCursor();
    // `localDataOwner` is deliberately left UNTOUCHED here — Phase G fix
    // design. Clearing it immediately (the previous behaviour) was itself
    // the source of the actual race this fix closes: the marker could
    // resolve before the reset it was meant to represent had genuinely
    // finished, so a fast-enough next login found `owner === null` and
    // skipped the very mismatch check that was supposed to catch it. It is
    // only ever overwritten once `settleLocalDatabaseFor()` — called from
    // `revalidate()`'s `authenticated` branch — has confirmed the reset
    // below actually completed for whichever account authenticates next.
    // Phase G-A: the primary cleanup path — clears this account's actual
    // data, not just the auth-adjacent metadata above, so whichever account
    // signs in next on this device starts genuinely empty. Fire-and-forget,
    // matching the calls above: the UI has already locked to the login
    // screen unconditionally, and nothing here needs to block that. Safe to
    // fire-and-forget specifically because `resetLocalDatabase()` is now
    // coalescing and durably state-tracked (see its own doc) — a next login
    // that arrives before this finishes joins the SAME in-flight reset via
    // `settleLocalDatabaseFor()` rather than racing it or skipping it.
    void resetLocalDatabase();
    void apiLogout().then((outcome) => {
      if (outcome === 'revoked') logoutRevocationPending.current = false;
    });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      ready: status !== 'checking',
      isAuthenticated: status === 'authenticated' || status === 'offline-authenticated',
      isOffline: status === 'offline-authenticated',
      role,
      mustChangePassword,
      login,
      logout,
      revalidate,
    }),
    [status, role, mustChangePassword, login, logout, revalidate],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used inside an AuthProvider');
  }
  return context;
}
